import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DashboardService, DashboardRow } from '../services/dashboard.service';
import { AuthService } from '../services/auth.service';

interface PieSlice {
	label: string;
	value: number;
	pct: number;        // 0..1
	color: string;
	pathD: string;      // SVG path
	labelX: number;
	labelY: number;
}

@Component({
	selector: 'app-dashboard',
	templateUrl: './dashboard.component.html',
	styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {

	// Charts state
	closedByReason: PieSlice[] = [];
	closedTotal: number = 0;

	activeByLawyer: { label: string; value: number; pct: number; color: string; lawyerId: number }[] = [];
	activeByLawyerPie: PieSlice[] = [];
	activeByLawyerMax: number = 1;
	activeLawyerTotal: number = 0;

	// Претенции по адвокат (vid_sluchaj.broiSeVPretentsii)
	pretentsiiByLawyer: { label: string; value: number; pct: number; color: string; lawyerId: number }[] = [];
	pretentsiiByLawyerPie: PieSlice[] = [];
	pretentsiiByLawyerMax: number = 1;
	pretentsiiLawyerTotal: number = 0;

	activeByVidSluchaj: PieSlice[] = [];
	activeVidSluchajTotal: number = 0;

	excelDownloading: boolean = false;

	// Палитри
	private readonly palettePie = [
		'#3498db', '#2ecc71', '#e74c3c', '#f39c12', '#9b59b6',
		'#1abc9c', '#34495e', '#e67e22', '#95a5a6', '#16a085'
	];
	private readonly paletteBar = [
		'#3498db', '#5dade2', '#85c1e9', '#aed6f1', '#d6eaf8',
		'#2980b9', '#1f618d', '#154360', '#7fb3d5', '#a9cce3',
		'#21618c', '#5499c7', '#85929e', '#aab7b8', '#d5d8dc'
	];

	constructor(private dashboardService: DashboardService,
		private authService: AuthService,
		private router: Router) { }

	ngOnInit() {
		// Dashboard-ът е само за админ — non-admin deep-link → пращаме към делата.
		if (!this.authService.isUserAdmin()) {
			this.router.navigateByUrl('/cases');
			return;
		}
		this.loadAll();
	}

	refresh() {
		this.loadAll();
	}

	/**
	 * Сваля цялото табло като Excel файл — 4 листа за 4-те графики.
	 * Backend-ът връща готов .xlsx + Content-Disposition с подходящо име;
	 * ние просто създаваме обектен URL и кликваме невидим линк.
	 */
	downloadExcel() {
		if (this.excelDownloading) { return; }
		this.excelDownloading = true;
		this.dashboardService.exportExcel().subscribe(
			response => {
				const blob = response.body;
				// Опит да извадим filename от Content-Disposition; иначе fallback.
				const cd = response.headers.get('Content-Disposition') || '';
				const m = cd.match(/filename="?([^"]+)"?/);
				const filename = m ? m[1] : 'dashboard.xlsx';

				const url = window.URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = filename;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				window.URL.revokeObjectURL(url);
				this.excelDownloading = false;
			},
			err => {
				console.error('Excel download failed', err);
				this.excelDownloading = false;
			}
		);
	}

	/**
	 * Двоен клик на бар → отваря /cases в нов таб с готови филтри и
	 * изпълнено търсене: този адвокат + само активни + дела/претенции.
	 * type: 'dela' (delaPretencii=1) | 'pretencii' (delaPretencii=2).
	 */
	openCasesForLawyer(bar: { lawyerId: number }, type: 'dela' | 'pretencii') {
		// lawyerId == null → '(БЕЗ ОТГОВОРНИК)' → responsible = -1
		const responsible = bar.lawyerId != null ? bar.lawyerId : -1;
		const dp = type === 'dela' ? 1 : 2;
		const url = `/cases?responsible=${responsible}&prikljucheno=2&delaPretencii=${dp}&autoSearch=1`;
		window.open(url, '_blank');
	}

	private loadAll() {
		this.dashboardService.closedByReason().subscribe(rows => {
			this.closedByReason = this.toPie(rows, 'reason', this.palettePie);
			this.closedTotal = this.sum(rows);
		});
		this.dashboardService.activeByLawyer().subscribe(rows => {
			this.activeLawyerTotal = this.sum(rows);
			const total = Math.max(1, this.activeLawyerTotal);
			this.activeByLawyerMax = Math.max(...rows.map(r => Number(r.count)), 1);
			this.activeByLawyer = rows.map((r, i) => ({
				label: String(r['lawyer']),
				value: Number(r.count),
				pct: Number(r.count) / total,
				// Цвят: ако адвокатът има личен color в preferences, ползваме него,
				// иначе палитрата fallback.
				color: this.normalizeColor(r['color']) || this.paletteBar[i % this.paletteBar.length],
				lawyerId: r['lawyerId'] != null ? Number(r['lawyerId']) : null
			}));
			// Същите данни като pie — с цвета на адвоката, ако има такъв.
			this.activeByLawyerPie = this.toPie(rows, 'lawyer', this.palettePie,
				(r, i) => this.normalizeColor(r['color']) || this.palettePie[i % this.palettePie.length]);
		});
		this.dashboardService.activeByLawyerPretentsii().subscribe(rows => {
			this.pretentsiiLawyerTotal = this.sum(rows);
			const total = Math.max(1, this.pretentsiiLawyerTotal);
			this.pretentsiiByLawyerMax = Math.max(...rows.map(r => Number(r.count)), 1);
			this.pretentsiiByLawyer = rows.map((r, i) => ({
				label: String(r['lawyer']),
				value: Number(r.count),
				pct: Number(r.count) / total,
				color: this.normalizeColor(r['color']) || this.paletteBar[i % this.paletteBar.length],
				lawyerId: r['lawyerId'] != null ? Number(r['lawyerId']) : null
			}));
			this.pretentsiiByLawyerPie = this.toPie(rows, 'lawyer', this.palettePie,
				(r, i) => this.normalizeColor(r['color']) || this.palettePie[i % this.palettePie.length]);
		});
		this.dashboardService.activeByVidSluchaj().subscribe(rows => {
			this.activeByVidSluchaj = this.toPie(rows, 'vidSluchaj', this.palettePie);
			this.activeVidSluchajTotal = this.sum(rows);
		});
	}

	private sum(rows: DashboardRow[]): number {
		return rows.reduce((acc, r) => acc + Number(r.count), 0);
	}

	/**
	 * Превръща списък от агрегации в pie slice-ове с SVG path-ове.
	 * SVG: радиус 90, център (100,100).
	 */
	/** Нормализира HEX цвят — игнорира '#FFFFFF' / null / празен (default бели се сливат с фона). */
	private normalizeColor(raw: any): string | null {
		if (!raw) return null;
		const c = String(raw).trim();
		if (!c) return null;
		// Default '#FFFFFF' / '#ffffff' значи 'няма зададен' — fallback на палитрата
		if (c.toLowerCase() === '#ffffff' || c.toLowerCase() === '#fff') return null;
		return c;
	}

	private toPie(
		rows: DashboardRow[],
		labelKey: string,
		palette: string[],
		colorPicker?: (row: DashboardRow, index: number) => string
	): PieSlice[] {
		const total = Math.max(1, this.sum(rows));
		const cx = 100;
		const cy = 100;
		const r = 90;
		let acc = 0;
		return rows.map((row, i) => {
			const value = Number(row.count);
			const pct = value / total;
			const startAngle = acc * 2 * Math.PI;
			const endAngle = (acc + pct) * 2 * Math.PI;
			acc += pct;

			const x1 = cx + r * Math.sin(startAngle);
			const y1 = cy - r * Math.cos(startAngle);
			const x2 = cx + r * Math.sin(endAngle);
			const y2 = cy - r * Math.cos(endAngle);
			const largeArc = pct > 0.5 ? 1 : 0;

			const pathD = pct >= 0.9999
				// Едно-парче 'пай' (целият кръг) — рисуваме два полу-кръга
				? `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx - 0.01} ${cy - r} Z`
				: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;

			// Label позиция в средата на дъгата
			const midAngle = (startAngle + endAngle) / 2;
			const labelR = r * 0.6;
			const labelX = cx + labelR * Math.sin(midAngle);
			const labelY = cy - labelR * Math.cos(midAngle);

			return {
				label: String(row[labelKey]),
				value,
				pct,
				color: colorPicker ? colorPicker(row, i) : palette[i % palette.length],
				pathD,
				labelX,
				labelY
			};
		});
	}
}
